"use client"

import * as React from "react"
import { Avatar as AvatarPrimitive } from "radix-ui"

import { cn } from "../../lib/utils"
import defaultUserAvatar from "../../../assets/images/default-user-avatar.png"

function Avatar({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & {
  size?: "default" | "sm" | "lg"
}) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size}
      className={cn(
        "after:border-border group/avatar relative flex size-8 shrink-0 rounded-full select-none after:absolute after:inset-0 after:rounded-full after:border after:mix-blend-darken data-[size=lg]:size-10 data-[size=sm]:size-6 dark:after:mix-blend-lighten",
        className
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn(
        "aspect-square size-full rounded-full object-cover",
        className
      )}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted text-muted-foreground flex size-full items-center justify-center rounded-full text-sm group-data-[size=sm]/avatar:text-xs",
        className
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "bg-primary text-primary-foreground ring-background absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-blend-color ring-2 select-none",
        "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden",
        "group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2",
        "group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2",
        className
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "*:data-[slot=avatar]:ring-background group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2",
        className
      )}
      {...props}
    />
  )
}

function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "bg-muted text-muted-foreground ring-background relative flex size-8 shrink-0 items-center justify-center rounded-full text-sm ring-2 group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3",
        className
      )}
      {...props}
    />
  )
}

/** Up-to-two-letter initials from a person's name; "?" when there's nothing usable. */
function getUserInitials(name: string | null | undefined): string {
  const parts = (name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? "")
  return parts.join("").toUpperCase() || "?"
}

type UserAvatarProps = Omit<React.ComponentProps<typeof Avatar>, "children"> & {
  /** The person's display name — drives the initials fallback and the image alt text. */
  name?: string | null
  /** The person's photo URL. When absent (or it fails to load) the initials fallback shows. */
  src?: string | null
  /** Override the fallback content (e.g. an icon when there is no person). Defaults to initials. */
  fallback?: React.ReactNode
  /** Extra classes for the fallback (e.g. custom text size). */
  fallbackClassName?: string
}

/**
 * The one common avatar for showing a person/user everywhere in the app: photo with an automatic
 * initials fallback, sized via the `size` prop (sm / default / lg). Use this instead of hand-rolling
 * Avatar + AvatarImage + AvatarFallback + initials at each call site.
 */
function UserAvatar({ name, src, fallback, fallbackClassName, size, ...props }: UserAvatarProps) {
  // With no photo, fall back to the shared default user-avatar image — unless the caller supplied a custom
  // `fallback` (e.g. a "no person" icon). Initials show only if even the default image fails to load.
  const resolvedSrc = src || (fallback ? undefined : defaultUserAvatar)
  return (
    <Avatar size={size} {...props}>
      {resolvedSrc ? <AvatarImage src={resolvedSrc} alt={name ?? ""} /> : null}
      <AvatarFallback className={fallbackClassName}>{fallback ?? getUserInitials(name)}</AvatarFallback>
    </Avatar>
  )
}

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarBadge,
  UserAvatar,
  getUserInitials,
}
